import type { ComponentNode } from '@api/types.gen'

export interface SlotVisibilityContext {
  /** Editor canvas — where an unfilled ComponentSlot still draws a drop placeholder. */
  isEditor: boolean
  /** Editor-only per-node force show/hide. Always empty in a built pipe. */
  override: (nodeId: string) => 'show' | 'hide' | undefined
  /** Editor's "ignore all visibility rules" toggle. */
  disableAllRules: boolean
  /** Fillers the enclosing SharedNodeRef instance supplied for a ComponentSlot's name. */
  fillersFor: (node: ComponentNode) => ComponentNode[]
  /** False only where the node's rules evaluated false this tick — an id nobody has
      evaluated yet answers true, so a band never collapses out from under live content. */
  ruleShows: (nodeId: string) => boolean
}

function willRender(node: ComponentNode, ctx: SlotVisibilityContext): boolean {
  const override = ctx.override(node.nodeId)
  if (override === 'hide') return false
  if (override === 'show') return true
  // Both are resolved ahead of RenderComponents' rules gate, so neither answers
  // to a rule of its own. A SharedNodeRef's template is not walked from here.
  if (node.type === 'SharedNodeRef') return true
  if (node.type === 'ComponentSlot') {
    const fillers = ctx.fillersFor(node)
    // Unfilled — this instance filled a different slot name, or none at all —
    // the slot is the editor's dashed placeholder and nothing in a build.
    if (!fillers.length) return ctx.isEditor
    return fillers.some((filler) => willRender(filler, ctx))
  }
  return ctx.disableAllRules || ctx.ruleShows(node.nodeId)
}

/**
 * Will anything in this slot actually render?
 *
 * For a layout component that drops the band/section wrapping a slot rather
 * than rendering it empty — the wrapper's own padding and background would
 * otherwise show as a stripe around nothing. Re-derived here instead of
 * measured off the rendered DOM so the editor canvas and the built pipe agree:
 * in editor mode RenderSlot always emits its drop-target wrapper, so a band
 * would never look empty there.
 *
 * Mirrors the gate in RenderComponents.vue; `isNodeShown` in PreviewApp.vue is
 * a third copy. Edit them together.
 */
export function slotHasVisibleChild(nodes: ComponentNode[], ctx: SlotVisibilityContext): boolean {
  return nodes.some((node) => willRender(node, ctx))
}
